import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useFindMany } from '@gadgetinc/react';
import { api } from '../api';

export default function AdminAnalytics() {
    const navigate = useNavigate();
    const [exportLoading, setExportLoading] = useState(false);

    // Fetch data for analytics
    const [{ data: users, fetching: usersFetching, error: usersError }] = useFindMany(api.user, {
        select: {
            id: true,
            approvalStatus: true,
            createdAt: true,
            lastSignedIn: true,
            assistantAccessMode: true,
            email: true,
            firstName: true,
            lastName: true
        }
    });

    const [{ data: sessions, fetching: sessionsFetching, error: sessionsError }] = useFindMany(api.session, {
        select: {
            id: true,
            createdAt: true,
            updatedAt: true,
            user: {
                id: true,
                email: true
            }
        }
    });

    const [{ data: assistants, fetching: assistantsFetching, error: assistantsError }] = useFindMany(api.assistant, {
        select: {
            id: true,
            name: true,
            createdAt: true,
            threads: {
                edges: {
                    node: {
                        id: true,
                        createdAt: true
                    }
                }
            }
        }
    });

    // Calculate statistics
    const userStats = React.useMemo(() => {
        if (!users) return { pending: 0, approved: 0, denied: 0, total: 0 };
        
        const stats = users.reduce((acc, user) => {
            acc[user.approvalStatus] = (acc[user.approvalStatus] || 0) + 1;
            acc.total += 1;
            return acc;
        }, { pending: 0, approved: 0, denied: 0, total: 0 });
        
        return stats;
    }, [users]);

    const activeSessionsCount = React.useMemo(() => {
        if (!sessions) return 0;
        // Consider sessions active if updated within last 24 hours
        const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
        return sessions.filter(session => new Date(session.updatedAt) > oneDayAgo).length;
    }, [sessions]);

    const assistantUsage = React.useMemo(() => {
        if (!assistants) return [];
        
        return assistants.map(assistant => ({
            name: assistant.name,
            threadCount: assistant.threads?.edges?.length || 0
        })).sort((a, b) => b.threadCount - a.threadCount).slice(0, 5);
    }, [assistants]);

    const recentActivityTrend = React.useMemo(() => {
        if (!users) return { today: 0, thisWeek: 0, thisMonth: 0 };
        
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
        
        return users.reduce((acc, user) => {
            const createdAt = new Date(user.createdAt);
            if (createdAt >= today) acc.today += 1;
            if (createdAt >= weekAgo) acc.thisWeek += 1;
            if (createdAt >= monthAgo) acc.thisMonth += 1;
            return acc;
        }, { today: 0, thisWeek: 0, thisMonth: 0 });
    }, [users]);

    const handleExportData = async () => {
        setExportLoading(true);
        try {
            const exportData = {
                users: users || [],
                sessions: sessions || [],
                assistants: assistants || [],
                statistics: {
                    userStats,
                    activeSessionsCount,
                    assistantUsage,
                    recentActivityTrend
                },
                exportedAt: new Date().toISOString()
            };
            
            const dataStr = JSON.stringify(exportData, null, 2);
            const dataBlob = new Blob([dataStr], { type: 'application/json' });
            const url = URL.createObjectURL(dataBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `analytics-export-${new Date().toISOString().split('T')[0]}.json`;
            link.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Export failed:', error);
            alert('Export failed. Please try again.');
        } finally {
            setExportLoading(false);
        }
    };

    const loading = usersFetching || sessionsFetching || assistantsFetching;
    const error = usersError || sessionsError || assistantsError;

    if (error) {
        return (
            <div style={{ 
                backgroundColor: '#111111', 
                minHeight: '100vh', 
                padding: '20px',
                color: 'white',
                fontFamily: 'Arial, sans-serif'
            }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                    <Link to="/admin" style={{ color: '#60a5fa', textDecoration: 'none', marginBottom: '20px', display: 'inline-block' }}>
                        ← Back to Admin
                    </Link>
                    <h1 style={{ color: 'white', marginBottom: '20px' }}>Analytics Dashboard</h1>
                    <div style={{ 
                        backgroundColor: '#dc2626', 
                        padding: '20px', 
                        borderRadius: '8px',
                        color: 'white'
                    }}>
                        Error loading analytics data: {error.toString()}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={{ 
            backgroundColor: '#111111', 
            minHeight: '100vh', 
            padding: '20px',
            color: 'white',
            fontFamily: 'Arial, sans-serif'
        }}>
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                {/* Header */}
                <div style={{ marginBottom: '30px' }}>
                    <Link to="/admin" style={{ 
                        color: '#60a5fa', 
                        textDecoration: 'none', 
                        marginBottom: '20px', 
                        display: 'inline-block',
                        fontSize: '16px'
                    }}>
                        ← Back to Admin
                    </Link>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h1 style={{ color: 'white', margin: 0 }}>Analytics Dashboard</h1>
                        <button
                            onClick={handleExportData}
                            disabled={loading || exportLoading}
                            style={{
                                backgroundColor: '#059669',
                                color: 'white',
                                border: 'none',
                                padding: '10px 20px',
                                borderRadius: '6px',
                                cursor: loading || exportLoading ? 'not-allowed' : 'pointer',
                                fontSize: '14px',
                                opacity: loading || exportLoading ? 0.6 : 1
                            }}
                        >
                            {exportLoading ? 'Exporting...' : 'Export Data'}
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
                        Loading analytics data...
                    </div>
                ) : (
                    <>
                        {/* Statistics Cards */}
                        <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                            gap: '20px',
                            marginBottom: '40px'
                        }}>
                            {/* User Statistics Card */}
                            <div style={{ 
                                backgroundColor: '#1e1e1e', 
                                padding: '24px', 
                                borderRadius: '12px',
                                border: '1px solid #374151'
                            }}>
                                <h3 style={{ color: 'white', marginTop: 0, marginBottom: '16px' }}>User Statistics</h3>
                                <div style={{ color: '#9ca3af', fontSize: '14px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span>Total Users:</span>
                                        <span style={{ color: 'white', fontWeight: 'bold' }}>{userStats.total}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span>Approved:</span>
                                        <span style={{ color: '#10b981', fontWeight: 'bold' }}>{userStats.approved}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span>Pending:</span>
                                        <span style={{ color: '#f59e0b', fontWeight: 'bold' }}>{userStats.pending}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span>Denied:</span>
                                        <span style={{ color: '#ef4444', fontWeight: 'bold' }}>{userStats.denied}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Active Sessions Card */}
                            <div style={{ 
                                backgroundColor: '#1e1e1e', 
                                padding: '24px', 
                                borderRadius: '12px',
                                border: '1px solid #374151'
                            }}>
                                <h3 style={{ color: 'white', marginTop: 0, marginBottom: '16px' }}>Active Sessions</h3>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#60a5fa' }}>
                                        {activeSessionsCount}
                                    </div>
                                    <div style={{ color: '#9ca3af', fontSize: '14px', marginTop: '8px' }}>
                                        Active in last 24 hours
                                    </div>
                                </div>
                            </div>

                            {/* Recent Activity Card */}
                            <div style={{ 
                                backgroundColor: '#1e1e1e', 
                                padding: '24px', 
                                borderRadius: '12px',
                                border: '1px solid #374151'
                            }}>
                                <h3 style={{ color: 'white', marginTop: 0, marginBottom: '16px' }}>Recent Activity</h3>
                                <div style={{ color: '#9ca3af', fontSize: '14px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span>Today:</span>
                                        <span style={{ color: 'white', fontWeight: 'bold' }}>{recentActivityTrend.today}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span>This Week:</span>
                                        <span style={{ color: 'white', fontWeight: 'bold' }}>{recentActivityTrend.thisWeek}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span>This Month:</span>
                                        <span style={{ color: 'white', fontWeight: 'bold' }}>{recentActivityTrend.thisMonth}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Popular Assistants */}
                        <div style={{ marginBottom: '40px' }}>
                            <div style={{ 
                                backgroundColor: '#1e1e1e', 
                                padding: '24px', 
                                borderRadius: '12px',
                                border: '1px solid #374151'
                            }}>
                                <h3 style={{ color: 'white', marginTop: 0, marginBottom: '20px' }}>Popular Assistants</h3>
                                {assistantUsage.length > 0 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                        {assistantUsage.map((assistant, index) => (
                                            <div key={index} style={{ 
                                                display: 'flex', 
                                                justifyContent: 'space-between', 
                                                alignItems: 'center',
                                                padding: '12px',
                                                backgroundColor: '#374151',
                                                borderRadius: '8px'
                                            }}>
                                                <span style={{ color: 'white', fontWeight: '500' }}>
                                                    {assistant.name || 'Unnamed Assistant'}
                                                </span>
                                                <span style={{ 
                                                    color: '#60a5fa', 
                                                    fontWeight: 'bold',
                                                    fontSize: '14px'
                                                }}>
                                                    {assistant.threadCount} threads
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div style={{ color: '#9ca3af', textAlign: 'center', padding: '20px' }}>
                                        No assistant usage data available
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Charts Section - Placeholder */}
                        <div style={{ marginBottom: '40px' }}>
                            <div style={{ 
                                backgroundColor: '#1e1e1e', 
                                padding: '24px', 
                                borderRadius: '12px',
                                border: '1px solid #374151'
                            }}>
                                <h3 style={{ color: 'white', marginTop: 0, marginBottom: '20px' }}>Analytics Charts</h3>
                                <div style={{ 
                                    textAlign: 'center', 
                                    padding: '60px 20px',
                                    backgroundColor: '#374151',
                                    borderRadius: '8px',
                                    color: '#9ca3af'
                                }}>
                                    <div style={{ fontSize: '18px', marginBottom: '8px' }}>📊</div>
                                    <div>Charts and graphs will be displayed here</div>
                                    <div style={{ fontSize: '14px', marginTop: '8px' }}>
                                        Coming soon: User registration trends, usage patterns, and more
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* System Metrics */}
                        <div>
                            <div style={{ 
                                backgroundColor: '#1e1e1e', 
                                padding: '24px', 
                                borderRadius: '12px',
                                border: '1px solid #374151'
                            }}>
                                <h3 style={{ color: 'white', marginTop: 0, marginBottom: '20px' }}>System Metrics</h3>
                                <div style={{ 
                                    display: 'grid', 
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                                    gap: '16px'
                                }}>
                                    <div style={{ 
                                        padding: '16px', 
                                        backgroundColor: '#374151', 
                                        borderRadius: '8px',
                                        textAlign: 'center'
                                    }}>
                                        <div style={{ color: '#9ca3af', fontSize: '14px' }}>Total Assistants</div>
                                        <div style={{ color: 'white', fontSize: '24px', fontWeight: 'bold', marginTop: '4px' }}>
                                            {assistants?.length || 0}
                                        </div>
                                    </div>
                                    <div style={{ 
                                        padding: '16px', 
                                        backgroundColor: '#374151', 
                                        borderRadius: '8px',
                                        textAlign: 'center'
                                    }}>
                                        <div style={{ color: '#9ca3af', fontSize: '14px' }}>Total Sessions</div>
                                        <div style={{ color: 'white', fontSize: '24px', fontWeight: 'bold', marginTop: '4px' }}>
                                            {sessions?.length || 0}
                                        </div>
                                    </div>
                                    <div style={{ 
                                        padding: '16px', 
                                        backgroundColor: '#374151', 
                                        borderRadius: '8px',
                                        textAlign: 'center'
                                    }}>
                                        <div style={{ color: '#9ca3af', fontSize: '14px' }}>Data Last Updated</div>
                                        <div style={{ color: 'white', fontSize: '14px', fontWeight: 'bold', marginTop: '4px' }}>
                                            {new Date().toLocaleTimeString()}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}